import React, { useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from "chart.js";
import { useFinance } from "../contexts/FinanceContext";
import { format, isAfter, subDays } from "date-fns";
import { useCurrency } from "../utils/useCurrency";

// 1. Import useTranslation hook
import { useTranslation } from "react-i18next";

import { FaPiggyBank, FaMoneyBillWave, FaStar } from "react-icons/fa";
// icons
import {
  FaUtensils,
  FaHome,
  FaCar,
  FaTshirt,
  FaBook,
  FaGift,
  FaPlane,
  FaHeartbeat,
  FaGem,
  FiTrendingUp,
  FiTrendingDown,
  FiDownload,
  FiFilter,
  FiChevronRight,
} from "react-icons/fa";
import { MdChevronRight } from "react-icons/md";
import { IoMdTrendingUp, IoMdTrendingDown } from "react-icons/io";
import { IoGameController } from "react-icons/io5";
import { FaDownload } from "react-icons/fa";
import { FaFilterCircleDollar } from "react-icons/fa6";
import BestDayWorstDay from "../components/BestDayWorstDay";
import ExpenseLimit from "../components/ExpenseLimit";
import SpendingsFinanceComp from "../components/SpendingsFinanceComp";
import Navbar from "../components/Navbar";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
);

const categoryIconMap = {
  "Food / Drinks": FaUtensils,
  "Home / Rent": FaHome,
  "Car & Fuel": FaCar,
  Clothes: FaTshirt,
  Entertainment: IoGameController,
  Education: FaBook,
  Gifts: FaGift,
  Travel: FaPlane,
  Health: FaHeartbeat,
  Beauty: FaGem,
};

export default function GrowthFinances({ theme }) {
  // 2. Use the hook instead of the direct instance
  // This hook ensures the page re-renders instantly when language changes
  const { t } = useTranslation();

  const { history = [], loading } = useFinance();
  const { formatCurrency } = useCurrency();
  const [query, setQuery] = useState("");
  const [timeRange, setTimeRange] = useState("all");
  const [activeView, setActiveView] = useState("all");
  const [expandedTransaction, setExpandedTransaction] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Filter transactions based on time range
  const filteredHistory = useMemo(() => {
    if (!history || history.length === 0) return [];

    if (timeRange === "all") return history;

    const now = new Date();
    let cutoffDate;

    switch (timeRange) {
      case "7days":
        cutoffDate = subDays(now, 7);
        break;
      case "30days":
        cutoffDate = subDays(now, 30);
        break;
      case "90days":
        cutoffDate = subDays(now, 90);
        break;
      default:
        return history;
    }

    return history.filter((transaction) => {
      const transactionDate = new Date(transaction.date);
      return isAfter(transactionDate, cutoffDate);
    });
  }, [history, timeRange]);

  // transactions sorted by date desc (using filteredHistory)
  const transactions = useMemo(() => {
    const copy = (filteredHistory || []).slice();
    copy.sort((a, b) => new Date(b.date) - new Date(a.date));
    return copy;
  }, [filteredHistory]);

  // Filter by type (income/spending)
  const filteredByType = useMemo(() => {
    if (activeView === "all") return transactions;
    return transactions.filter((t) => t.type === activeView);
  }, [transactions, activeView]);

  // totals (using filteredHistory)
  const totals = useMemo(() => {
    let income = 0;
    let spending = 0;
    filteredHistory.forEach((h) => {
      if (h.type === "income") income += Number(h.amount || 0);
      if (h.type === "spending") spending += Number(h.amount || 0);
    });
    return { income, spending, balance: income - spending };
  }, [filteredHistory]);
  const isDark = localStorage.getItem("theme") === "dark";

  // category totals (using filteredHistory)
  const categoryTotals = useMemo(() => {
    const map = {};
    filteredHistory.forEach((h) => {
      if (h.type !== "spending") return;
      const name = h.category || "-";
      map[name] = (map[name] || 0) + Number(h.amount || 0);
    });
    return Object.entries(map)
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [filteredHistory]);

  const lineChartData = useMemo(() => {
    if (!filteredHistory || filteredHistory.length === 0) return null;

    const labels = filteredHistory.map((h) =>
      format(new Date(h.date), "MMM dd")
    );
    const values = filteredHistory.map((h) =>
      h.type === "income" ? Number(h.amount) : -Number(h.amount)
    );

    let runningBalance = 0;
    const running = values.map((v) => (runningBalance += v));

    return {
      labels,
      datasets: [
        {
          label: t("finances.balance"),
          data: running,
          borderColor: isDark ? "#38bdf8" : "#0ea5e9",
          backgroundColor: "rgba(56, 189, 248, 0.12)",
          borderWidth: 2,
          tension: 0.35,
          fill: true,
        },
      ],
    };
  }, [filteredHistory, isDark, t]);

  const gridColor = theme === "dark" ? "#363d52" : "#ddd";

  // pie chart data (using categoryTotals from filteredHistory)
  const pieData = useMemo(() => {
    const labels = categoryTotals.map((c) => c.name);
    const data = categoryTotals.map((c) => Math.round(c.amount * 100) / 100);
    const baseColors = [
      "#4f46e5",
      "#0ea5a3",
      "#f59e0b",
      "#ef4444",
      "#06b6d4",
      "#8b5cf6",
      "#10b981",
      "#f97316",
      "#a78bfa",
      "#ef9a9a",
    ];
    const backgroundColor = labels.map(
      (_, i) => baseColors[i % baseColors.length]
    );
    return { labels, datasets: [{ data, backgroundColor, hoverOffset: 8 }] };
  }, [categoryTotals]);

  const pieOptions = {
    plugins: {
      legend: { position: "right", labels: { boxWidth: 12, padding: 12 } },
    },
    maintainAspectRatio: false,
  };

  const topCategory = categoryTotals[0] || null;
  const totalSpent = totals.spending;

  const filteredTransactions = filteredByType.filter((t) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (t.category || "").toLowerCase().includes(q) ||
      (t.note || "").toLowerCase().includes(q) ||
      String(t.amount).toLowerCase().includes(q)
    );
  });

  const savingsRate = totals.income
    ? Math.round(((totals.income - totals.spending) / totals.income) * 100)
    : 0;

  const isMobileView = window.innerWidth <= 650;

  const handleTransactionClick = (id) => {
    setExpandedTransaction(expandedTransaction === id ? null : id);
  };

  // Animation
  const fadeInUp = keyframes`
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `;

  return (
    <PageContainer>
      <Header>
        <Title>{t("finances.title")}</Title>
        <Subtitle>{t("finances.subtitle")}</Subtitle>

        {/* Time Range Selector */}
        <TimeRangeSelector>
          <TimeRangeLabel>{t("finances.view")}:</TimeRangeLabel>
          <TimeRangeButtons>
            <TimeRangeButton
              active={timeRange === "all"}
              onClick={() => setTimeRange("all")}
            >
              {t("finances.allTime")}
            </TimeRangeButton>
            <TimeRangeButton
              active={timeRange === "90days"}
              onClick={() => setTimeRange("90days")}
            >
              {t("finances.last90Days")}
            </TimeRangeButton>
            <TimeRangeButton
              active={timeRange === "30days"}
              onClick={() => setTimeRange("30days")}
            >
              {t("finances.last30Days")}
            </TimeRangeButton>
            <TimeRangeButton
              active={timeRange === "7days"}
              onClick={() => setTimeRange("7days")}
            >
              {t("finances.last7Days")}
            </TimeRangeButton>
          </TimeRangeButtons>
        </TimeRangeSelector>
      </Header>

      <Insights className="insights" style={{ transition: "0.5s " }}>
        <InsightsGrid style={{ transition: "0.5s" }}>
          <InsightBox style={{ transition: "0.5s " }}>
            <InsightLabel>
              <FaMoneyBillWave style={{ marginRight: 6 }} />{" "}
              {t("finances.totalIncome")}
            </InsightLabel>
            <InsightValue color="#10b981">
              {formatCurrency(totals.income)}
            </InsightValue>
            <InsightSub>
              {filteredHistory.length
                ? `${t("finances.avgPerTransaction")}: ${formatCurrency(
                    totals.income / filteredHistory.length
                  )}`
                : "—"}
            </InsightSub>
          </InsightBox>

          <InsightBox>
            <InsightLabel>
              <FaMoneyBillWave style={{ marginRight: 6 }} />{" "}
              {t("finances.totalSpent")}
            </InsightLabel>
            <InsightValue color="#ef4444">
              {formatCurrency(totalSpent)}
            </InsightValue>
            <InsightSub>
              {t("finances.top")}:{" "}
              {topCategory
                ? `${topCategory.name} • ${formatCurrency(topCategory.amount)}`
                : "—"}
            </InsightSub>
          </InsightBox>

          <InsightBox>
            <InsightLabel>
              <FaStar style={{ marginRight: 6 }} /> {t("finances.topCategory")}
            </InsightLabel>
            <InsightValue color="#38bdf8">
              {topCategory
                ? `${topCategory.name} • ${Math.round(
                    (topCategory.amount / (totalSpent || 1)) * 100
                  )}%`
                : "—"}
            </InsightValue>
            <InsightSub>
              {topCategory
                ? `${formatCurrency(topCategory.amount)} ${t("finances.spent")}`
                : "—"}
            </InsightSub>
          </InsightBox>

          <InsightBox>
            <InsightLabel>
              <FaPiggyBank style={{ marginRight: 6 }} />{" "}
              {t("finances.savingsRate")}
            </InsightLabel>
            <InsightValue color={savingsRate >= 50 ? "#10b981" : "#ef4444"}>
              {savingsRate}%
            </InsightValue>
            <InsightSub>
              {formatCurrency(totals.income - totals.spending)}{" "}
              {t("finances.saved")}
            </InsightSub>
          </InsightBox>
        </InsightsGrid>
      </Insights>

      <TransactionsSection>
        <TransactionsCard className="transactionsCard">
          <TransactionsHeader>
            <div>
              <SectionTitle>{t("finances.transactions")}</SectionTitle>
              <TransactionsCount>
                {filteredTransactions.length} transactions
              </TransactionsCount>
            </div>
            <HeaderActions>
              <TransactionFilters>
                <FilterButton
                  active={activeView === "all"}
                  onClick={() => setActiveView("all")}
                >
                  All
                </FilterButton>
                <FilterButton
                  active={activeView === "income"}
                  onClick={() => setActiveView("income")}
                  income
                >
                  <IoMdTrendingUp />
                  Income
                </FilterButton>
                <FilterButton
                  active={activeView === "spending"}
                  onClick={() => setActiveView("spending")}
                  spending
                >
                  <IoMdTrendingDown />
                  Spending
                </FilterButton>
              </TransactionFilters>
              {/* {!isMobileView && (
                <ActionButtons>
                  <ActionButton>
                    <FaFilterCircleDollar />
                    <span>Filter</span>
                  </ActionButton>
                  <ActionButton>
                    <FaDownload />
                    <span>Export</span>
                  </ActionButton>
                </ActionButtons>
              )} */}
            </HeaderActions>
          </TransactionsHeader>

          <TransactionsList>
            {loading ? (
              <EmptyState>
                <LoadingSpinner />
                <span>{t("finances.loading")}</span>
              </EmptyState>
            ) : filteredTransactions.length === 0 ? (
              <EmptyState>
                <EmptyIllustration>📊</EmptyIllustration>
                <span>{t("finances.noTransactions")}</span>
              </EmptyState>
            ) : (
              filteredTransactions.map((transaction, i) => {
                const Icon = categoryIconMap[transaction.category] || FaGem;
                const isIncome = transaction.type === "income";
                const isExpanded = expandedTransaction === i;

                return (
                  <TransactionItem
                    key={i}
                    type={transaction.type}
                    isExpanded={isExpanded}
                    onClick={() => handleTransactionClick(i)}
                    // whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    style={{ height: "300px" }}
                  >
                    <TransactionContent>
                      <TransactionIcon type={transaction.type}>
                        {isIncome ? <IoMdTrendingUp /> : <IoMdTrendingDown />}
                      </TransactionIcon>
                      <TransactionInfo>
                        <TransactionName>
                          {transaction.note || "No description"}
                          <TransactionCategory>
                            <Icon />
                            <span>{transaction.category || "Unknown"}</span>
                          </TransactionCategory>
                        </TransactionName>
                        <TransactionMeta>
                          <span className="date">
                            {format(new Date(transaction.date), "MMM dd, yyyy")}
                          </span>
                          <span className="time">
                            {format(new Date(transaction.date), "HH:mm")}
                          </span>
                        </TransactionMeta>
                      </TransactionInfo>
                      <TransactionAmount type={transaction.type}>
                        {isIncome ? "+" : "-"}
                        {formatCurrency(transaction.amount)}
                        <ChevronIcon rotated={isExpanded}>
                          <MdChevronRight />
                        </ChevronIcon>
                      </TransactionAmount>
                    </TransactionContent>

                    <AnimatePresence>
                      {isExpanded && (
                        <TransactionDetails
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                        >
                          <DetailGrid>
                            <DetailItem>
                              <label>Transaction ID</label>
                              <span>#TRX-{String(i + 1).padStart(4, "0")}</span>
                            </DetailItem>
                            <DetailItem>
                              <label>Category</label>
                              <span>
                                <CategoryBadge type={transaction.type}>
                                  <Icon />
                                  {transaction.category || "-"}
                                </CategoryBadge>
                              </span>
                            </DetailItem>
                            <DetailItem>
                              <label>Date & Time</label>
                              <span>
                                {format(
                                  new Date(transaction.date),
                                  "EEEE, MMMM dd, yyyy 'at' HH:mm"
                                )}
                              </span>
                            </DetailItem>
                            <DetailItem>
                              <label>Amount</label>
                              <span
                                style={{
                                  color: isIncome ? "#10B981" : "#EF4444",
                                  fontWeight: 600,
                                }}
                              >
                                {isIncome ? "+" : "-"}
                                {formatCurrency(transaction.amount)}
                              </span>
                            </DetailItem>
                          </DetailGrid>
                          {transaction.note && (
                            <TransactionNote>
                              <label>Note</label>
                              <p>{transaction.note}</p>
                            </TransactionNote>
                          )}
                        </TransactionDetails>
                      )}
                    </AnimatePresence>
                  </TransactionItem>
                );
              })
            )}
          </TransactionsList>
        </TransactionsCard>
        <SpendingsFinanceComp className="balance" theme={theme} />
      </TransactionsSection>

      <BottomGrid>
        <SectionCard className="btgCard1">
          <SectionTitle>{t("finances.whereMoneyWent")}</SectionTitle>

          {categoryTotals.length === 0 ? (
            <EmptyRow style={{ height: 220 }}>
              {t("finances.noSpending")}
            </EmptyRow>
          ) : (
            <>
              <TotalSpent>
                {t("finances.totalSpent")}:{" "}
                <span>{formatCurrency(totalSpent)}</span>
              </TotalSpent>
              <PieContainer>
                <Pie data={pieData} options={pieOptions} />
                <Legenda>
                  {categoryTotals.map((c, i) => {
                    const color = pieData.datasets[0].backgroundColor[i];
                    return (
                      <LegendItem key={c.name}>
                        <ColorDot style={{ backgroundColor: color }} />
                        <LegendLabel>
                          {c.name}: {formatCurrency(c.amount)} (
                          {((c.amount / totalSpent) * 100).toFixed(0)}%)
                        </LegendLabel>
                      </LegendItem>
                    );
                  })}
                </Legenda>
              </PieContainer>
            </>
          )}
        </SectionCard>

        <LimitAndMax className="btgCard2">
          <ExpenseLimit history={history} />
          <BestDayWorstDay />
        </LimitAndMax>

        <SectionCard className="btgCard3">
          <SectionTitle>{t("finances.categoryBreakdown")}</SectionTitle>
          <CategoryList>
            {categoryTotals.length === 0 && (
              <EmptyRow>{t("finances.noCategories")}</EmptyRow>
            )}
            {categoryTotals.map((c) => {
              const percent = totalSpent
                ? ((c.amount / totalSpent) * 100).toFixed(0)
                : "0";
              const Icon = categoryIconMap[c.name] || FaGem;
              return (
                <CategoryRow key={c.name}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <Icon />
                    <div>
                      <div
                        style={{
                          fontWeight: 500,
                          color: "var(--primaryColor)",
                        }}
                      >
                        {c.name}
                      </div>
                      <div style={{ color: "#8f9bb3", fontSize: 13 }}>
                        {formatCurrency(c.amount)}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>{percent}%</div>
                </CategoryRow>
              );
            })}
          </CategoryList>
        </SectionCard>
      </BottomGrid>
    </PageContainer>
  );
}

/* ---------------- Styled Components ---------------- */

const PageContainer = styled.div`
  padding: 24px 0 24px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: 0.5s;
  margin: 0;
  @media (max-width: 1200px) {
    gap: 1px;
  }
  @media (max-width: 650px) {
    margin-top: 120px;
  }
`;

const Header = styled.header`
  margin-bottom: 0.5rem;
  margin-left: 5%;
  animation: fadeInUp 0.8s ease-out;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, var(--primaryColor), #8b5cf6);
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-family: "Inter", sans-serif;
`;

const Subtitle = styled.p`
  color: var(--fontSecondaryColor);
  font-size: 1rem;
  max-width: 600px;
  line-height: 1.6;
`;

// Time Range Selector Styles
const TimeRangeSelector = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
  gap: 12px;
  flex-wrap: wrap;
`;

const TimeRangeLabel = styled.span`
  font-size: 14px;
  color: var(--fontSecondaryColor);
  font-weight: 500;
`;

const TimeRangeButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TimeRangeButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  background: ${(props) =>
    props.active ? "var(--primaryColor)" : "var(--secondaryBackgroundColor)"};
  color: ${(props) => (props.active ? "white" : "var(--fontSecondaryColor)")};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.active ? "var(--primaryColor)" : "rgba(255, 255, 255, 0.05)"};
  }

  @media (max-width: 650px) {
    padding: 5px 10px;
    font-size: 12px;
  }
`;

const TransactionsSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 2em;
  transition: 0.5s;

  .transactionsCard {
    width: 57%;
    @media (max-width: 650px) {
      width: 100%;
    }
  }
  .balance {
    background: var(--secondaryBackgroundColor, #0f1724);
    padding: 3.2em;
    border-radius: 14px;
    color: var(--fontPrimaryColor, #fff);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
    transition: 0.5s;
    width: 43%;
    display: none;
  }
  @media (max-width: 1650px) {
    .transactionsCard {
      width: 100%;
    }
  }
  @media (max-width: 1200px) {
    gap: 1em;
  }
`;

const TransactionsCard = styled.div`
  background: var(--secondaryBackgroundColor, #0f1724);
  padding: 2.5em;
  border-radius: 20px;
  color: var(--fontPrimaryColor, #fff);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  animation: fadeInUp 0.8s ease-out 0.2s both;

  &:hover {
    box-shadow: 0 15px 50px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 650px) {
    padding: 1.5em;
    border-radius: 16px;
  }
`;

const TransactionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2em;
  flex-wrap: wrap;
  gap: 1.5em;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1em;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1.5em;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 4px 0;
  font-weight: 700;
  font-size: 1.5rem;
  color: var(--fontPrimaryColor);
`;

const TransactionsCount = styled.div`
  font-size: 0.875rem;
  color: var(--fontSecondaryColor);
`;

const TransactionFilters = styled.div`
  display: flex;
  gap: 0.5em;
  background: var(--thirdBackgroundColor);
  padding: 4px;
  border-radius: 12px;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  background: ${(props) =>
    props.active
      ? props.income
        ? "rgba(16, 185, 129, 0.2)"
        : props.spending
        ? "rgba(239, 68, 68, 0.2)"
        : "var(--primaryColor)"
      : "transparent"};
  color: ${(props) =>
    props.active
      ? props.income
        ? "#10B981"
        : props.spending
        ? "#EF4444"
        : "white"
      : "var(--fontSecondaryColor)"};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.income
        ? "rgba(16, 185, 129, 0.15)"
        : props.spending
        ? "rgba(239, 68, 68, 0.15)"
        : "rgba(146, 129, 221, 0.1)"};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75em;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: rgba(146, 129, 221, 0.1);
  border: 1px solid rgba(146, 129, 221, 0.2);
  border-radius: 8px;
  color: #9281dd;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(146, 129, 221, 0.2);
    border-color: #9281dd;
  }
`;

const TransactionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75em;
  max-height: 600px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(146, 129, 221, 0.5);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const TransactionItem = styled(motion.div)`
  min-height: 120px;
  background: ${(props) =>
    props.type === "income"
      ? "rgba(16, 185, 129, 0.05)"
      : "rgba(239, 68, 68, 0.05)"};
  border: 1px solid
    ${(props) =>
      props.type === "income"
        ? "rgba(16, 185, 129, 0.15)"
        : "rgba(239, 68, 68, 0.15)"};
  border-radius: 14px;
  padding: 1.25em;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    border-color: ${(props) =>
      props.type === "income" ? "#10B981" : "#EF4444"};
    background: ${(props) =>
      props.type === "income"
        ? "rgba(16, 185, 129, 0.08)"
        : "rgba(239, 68, 68, 0.08)"};
  }

  @media (max-width: 650px) {
    padding: 1em;
  }
`;

const TransactionContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1em;
  position: relative;
`;

const TransactionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.type === "income"
      ? "rgba(16, 185, 129, 0.15)"
      : "rgba(239, 68, 68, 0.15)"};
  color: ${(props) => (props.type === "income" ? "#10B981" : "#EF4444")};
  font-size: 1.25rem;

  @media (max-width: 650px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
`;

const TransactionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TransactionName = styled.div`
  font-weight: 600;
  color: var(--fontPrimaryColor);
  margin-bottom: 0.5em;
  font-size: 1rem;
  line-height: 1.4;
`;

const TransactionCategory = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  color: var(--fontSecondaryColor);
  margin-top: 4px;

  svg {
    font-size: 0.875rem;
  }
`;

const TransactionMeta = styled.div`
  display: flex;
  gap: 1em;
  font-size: 0.75rem;
  color: var(--fontSecondaryColor);

  .date {
    background: rgba(146, 129, 221, 0.1);
    padding: 0.25em 0.75em;
    border-radius: 100px;
  }

  .time {
    opacity: 0.8;
  }
`;

const TransactionAmount = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${(props) => (props.type === "income" ? "#10B981" : "#EF4444")};
  display: flex;
  align-items: center;
  gap: 1em;

  @media (max-width: 650px) {
    font-size: 1rem;
  }
`;

const ChevronIcon = styled.div`
  transition: transform 0.3s ease;
  transform: ${(props) => (props.rotated ? "rotate(90deg)" : "rotate(0deg)")};
  opacity: 0.6;
`;

const TransactionDetails = styled(motion.div)`
  margin-top: 1.5em;
  padding-top: 1.5em;
  border-top: 1px solid rgba(146, 129, 221, 0.1);
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5em;

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
    gap: 1em;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5em;

  label {
    font-size: 0.75rem;
    color: var(--fontSecondaryColor);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }

  span {
    font-size: 0.9375rem;
    color: var(--fontPrimaryColor);
  }
`;

const CategoryBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.5em 1em;
  background: ${(props) =>
    props.type === "income"
      ? "rgba(16, 185, 129, 0.15)"
      : "rgba(239, 68, 68, 0.15)"};
  color: ${(props) => (props.type === "income" ? "#10B981" : "#EF4444")};
  border-radius: 100px;
  font-size: 0.875rem;
  font-weight: 500;
`;

const TransactionNote = styled.div`
  margin-top: 1.5em;
  padding: 1em;
  background: var(--thirdBackgroundColor);
  border-radius: 8px;

  label {
    display: block;
    font-size: 0.75rem;
    color: var(--fontSecondaryColor);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
    margin-bottom: 0.5em;
  }

  p {
    margin: 0;
    font-size: 0.9375rem;
    color: var(--fontPrimaryColor);
    line-height: 1.5;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4em 2em;
  color: var(--fontSecondaryColor);
  text-align: center;
  gap: 1.5em;

  span {
    font-size: 1rem;
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(146, 129, 221, 0.1);
  border-top-color: #9281dd;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyIllustration = styled.div`
  font-size: 3rem;
  opacity: 0.5;
`;

const TotalSpent = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 16px;
  text-align: center;
  transition: 0.5;
  span {
    color: #ef4444;
    font-weight: 600;
    margin-left: 6px;
  }
`;

const PieContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
  margin-top: 5em;
  transition: 0.5s;
  @media (max-width: 1100px) {
    flex-direction: column;
    margin-top: 0;
    max-height: 600px;
  }
`;

const Legenda = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  transition: 0.5s;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  transition: 0.5s;
  gap: 10px;
`;

const LimitAndMax = styled.div`
  display: flex;
  gap: 2em;
  flex-direction: column;
`;

const ColorDot = styled.div`
  width: 18px;
  height: 14px;
  display: none;
  border-radius: 50%;
  border: 2px solid #df0f0f;
`;

const LegendLabel = styled.div`
  font-size: 14px;
  color: var(--fontPrimaryColor, #f81515);
`;

const SectionCard = styled.div`
  background: var(--secondaryBackgroundColor, #0f1724);
  padding: 3.2em;
  border-radius: 14px;
  color: var(--fontPrimaryColor, #fff);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: 0.5s;
  animation: fadeInUp 0.8s ease-out 0.4s both;

  .insights {
    /* background-color: red; */
  }

  @media (max-width: 650px) {
    padding: 2em;
  }
`;

const Balance = styled.div`
  background: var(--secondaryBackgroundColor, #0f1724);
  padding: 1.2em;
  border-radius: 14px;
  color: var(--fontPrimaryColor, #fff);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: 0.5s;
  width: 40%;
  .balance {
    height: auto;
    width: 100%;
  }
`;

const Insights = styled.div`
  padding: 16px;
  border-radius: 14px;
  color: var(--fontPrimaryColor, #fff);
  transition: 0.5s;
  animation: fadeInUp 0.8s ease-out 0.1s both;

  @media (max-width: 520px) {
    padding: 16px 0;
  }
`;

const InsightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2em;
  transition: 0.5s;
  @media (max-width: 1200px) {
    gap: 1em;
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 430px) {
    gap: 10px;
  }
`;

const InsightBox = styled.div`
  padding: 28px 30px;
  display: flex;
  transition: 0.5s;
  flex-direction: column;
  gap: 1em;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  background: var(--secondaryBackgroundColor);
  transition: all 0.3s ease;
  animation: fadeInUp 0.8s ease-out;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 430px) {
    padding: 20px;
  }
`;

const InsightLabel = styled.div`
  font-size: 15px;
  color: var(--fontSecondaryColor);
  @media (max-width: 430px) {
    font-size: 13px;
  }
`;

const InsightValue = styled.div`
  font-weight: 500;
  margin-top: 6px;
  margin-left: 10px;
  font-size: 28px;
  color: ${(props) => props.color || "inherit"};
  transition: 0.5s;
  @media (max-width: 1060px) {
    font-size: 24px;
  }
  @media (max-width: 430px) {
    font-size: 20px;
  }
`;

const InsightSub = styled.div`
  font-size: 14px;
  color: var(--fontSecondaryColor);
  margin-top: 15px;
  transition: 0.5s;
  @media (max-width: 430px) {
    font-size: 13px;
  }
`;

const BottomGrid = styled.div`
  display: flex;
  gap: 2em;
  margin-top: 1em;
  .btgCard1 {
    width: 32%;
    animation: fadeInUp 0.8s ease-out 0.6s both;
  }
  .btgCard2 {
    width: 32%;
    animation: fadeInUp 0.8s ease-out 0.7s both;
  }
  .btgCard3 {
    width: 32%;
    animation: fadeInUp 0.8s ease-out 0.8s both;
  }

  @media (max-width: 1700px) {
    flex-wrap: wrap;
    .btgCard1 {
      width: 54%;
    }
    .btgCard2 {
      width: 42%;
    }
    .btgCard3 {
      width: 100%;
    }
  }
  @media (max-width: 1030px) {
    flex-wrap: wrap;
    .btgCard1 {
      width: 100%;
    }
    .btgCard2 {
      width: 48%;
    }
    .btgCard3 {
      width: 49%;
    }
  }
  @media (max-width: 1200px) {
    gap: 1em;
  }
  @media (max-width: 800px) {
    .btgCard2 {
      width: 100%;
    }
    .btgCard3 {
      width: 100%;
      padding: 30px;
    }
  }
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 3em;
`;

const CategoryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  border-radius: 10px;
  background: var(--thirdBackgroundColor);
  transition: 0.5s;

  &:hover {
    background: rgba(146, 129, 221, 0.1);
    transform: translateX(5px);
  }
`;

const EmptyRow = styled.div`
  padding: 24px;
  color: #9aa4c0;
  text-align: center;
`;

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
